/**
 * Family Scoring — Priority and target tier computation
 *
 * Pure functions for scoring family priority and determining
 * target difficulty tiers based on mastery state.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 * v11.3: Added skill interleaving penalty (P-11).
 */

import type { FamilyDefinition } from '../../content';
import type { FamilyMasteryState } from '../familyMasteryEngine';
import { V11_CONFIG, debugLog } from './selectionConfig';

/**
 * Compute mastery confidence based on attempts and consistency.
 * Returns 0-1 score indicating how reliable the mastery score is.
 */
export function computeMasteryConfidence(masteryState: FamilyMasteryState | undefined): number {
  if (!masteryState || masteryState.attempts === 0) return 0;

  // Base confidence from attempt count
  const attemptConfidence = Math.min(1, masteryState.attempts / V11_CONFIG.MASTERY_CONFIDENCE_MIN_ATTEMPTS);

  // Combine with built-in confidence metric
  const combinedConfidence = (attemptConfidence + masteryState.confidence) / 2;
  return combinedConfidence;
}

/**
 * Check if a family's mastery is truly stable (not just high score with low attempts).
 */
export function isMasteryStable(masteryState: FamilyMasteryState | undefined): boolean {
  if (!masteryState) return false;
  if (masteryState.masteryScore < V11_CONFIG.MASTERED_THRESHOLD) return false;
  if (masteryState.attempts < V11_CONFIG.MASTERY_CONFIDENCE_MIN_ATTEMPTS) return false;
  const confidence = computeMasteryConfidence(masteryState);
  return confidence >= 0.6; // Require 60% confidence for stable mastery
}

/**
 * Compute a priority score for a family based on its mastery state.
 * Higher score = higher priority (more likely to be selected).
 *
 * v11.2: Enhanced with recency weighting and confidence checks.
 * v11.8: B/M/E proximity boost for new families (QF-26 Optie B).
 * v11.3: Skill interleaving penalty (P-11).
 *
 * NOTE: This function now receives mastery state directly (already group-scoped by caller).
 */
export function computeFamilyPriority(family: FamilyDefinition, masteryState: FamilyMasteryState | undefined, recentFamilyIds: Set<string>, periodTier?: number, recentSkills?: string[]): number {
  let score: number;
  if (!masteryState || masteryState.attempts === 0) {
    // Never attempted — highest priority (explore new content)
    score = 100;

    // v11.8: B/M/E proximity boost (QF-26 Optie B)
    // For new families, prefer those closer to the student's B/M/E period tier.
    // This makes "Begin groep 7" students see B7-tier families first.
    if (periodTier !== undefined) {
      const tierDistance = Math.abs(family.tier - periodTier);
      if (tierDistance === 0) {
        score += V11_CONFIG.BME_PROXIMITY_BOOST_EXACT;
        debugLog(`   📅 B/M/E boost +${V11_CONFIG.BME_PROXIMITY_BOOST_EXACT} for ${family.familyId} (exact match tier ${periodTier})`);
      } else if (tierDistance <= 1) {
        score += V11_CONFIG.BME_PROXIMITY_BOOST_NEAR;
        debugLog(`   📅 B/M/E boost +${V11_CONFIG.BME_PROXIMITY_BOOST_NEAR} for ${family.familyId} (±1 from tier ${periodTier})`);
      } else if (tierDistance <= 2) {
        score += V11_CONFIG.BME_PROXIMITY_BOOST_FAR;
        debugLog(`   📅 B/M/E boost +${V11_CONFIG.BME_PROXIMITY_BOOST_FAR} for ${family.familyId} (±2 from tier ${periodTier})`);
      }
    }
  } else if (masteryState.masteryScore < V11_CONFIG.STRUGGLING_THRESHOLD) {
    score = 90;
  } else if (masteryState.masteryScore < V11_CONFIG.LEARNING_THRESHOLD) {
    score = 70;
  } else if (masteryState.masteryScore < V11_CONFIG.MASTERED_THRESHOLD) {
    score = 40;
  } else {
    // Mastered, but check if it's stable
    const isStable = isMasteryStable(masteryState);
    score = isStable ? 10 : 30; // Unstable mastery gets higher priority
  }

  // v11.2: ENHANCED recency penalty (50% reduction)
  if (recentFamilyIds.has(family.familyId)) {
    score = score * V11_CONFIG.RECENCY_PENALTY_MULTIPLIER;
    debugLog(`   🕐 Recency penalty applied to ${family.familyId}: ${score * 2} → ${score}`);
  }

  // Confidence penalty: low confidence means the score is unreliable,
  // so we should test this family more
  const confidence = computeMasteryConfidence(masteryState);
  if (masteryState && confidence < 0.3 && masteryState.attempts > 0) {
    score += 10;
    debugLog(`   📊 Low confidence bonus for ${family.familyId}: +10 (confidence: ${confidence.toFixed(2)})`);
  }

  // v11.3: Skill interleaving penalty (P-11)
  // Penalize families whose skill matches recent session skills to promote
  // interleaved practice (Rohrer & Taylor, 2007).
  if (recentSkills && recentSkills.length > 0) {
    const window = recentSkills.slice(-V11_CONFIG.SKILL_INTERLEAVE_WINDOW);
    if (window.includes(family.skill)) {
      const before = score;
      score = score * V11_CONFIG.SKILL_INTERLEAVE_PENALTY;
      debugLog(`   🔀 Interleave penalty for ${family.familyId} (skill "${family.skill}" in recent window): ${before.toFixed(0)} → ${score.toFixed(0)}`);
    }
  }
  return Math.max(0, score);
}

/**
 * Determine the target difficulty tier for a family based on its mastery.
 * The tier is clamped to the allowed range for the selectedGroup.
 *
 * Strategy:
 * - mastery < 40 → family's base tier (practice fundamentals)
 * - mastery 40-70 → base tier + 0 to +1 (consolidate)
 * - mastery > 70 → base tier + 1 to +2 (challenge)
 */
export function determineTargetTier(family: FamilyDefinition, masteryState: FamilyMasteryState | undefined, allowedTiers: Set<number>): number {
  const baseTier = family.tier;
  let targetTier: number;
  if (!masteryState || masteryState.attempts === 0) {
    // New family: start at base tier
    targetTier = baseTier;
  } else {
    const mastery = masteryState.masteryScore;
    if (mastery < V11_CONFIG.STRUGGLING_THRESHOLD) {
      // < 40: struggling — practice at base tier
      targetTier = baseTier;
    } else if (mastery < 55) {
      // 40–54: consolidating — still at base tier
      targetTier = baseTier;
    } else if (mastery < V11_CONFIG.LEARNING_THRESHOLD) {
      // 55–69: advancing — base + 1 (plus variant)
      targetTier = baseTier + 1;
    } else if (mastery < V11_CONFIG.MASTERED_THRESHOLD) {
      // 70–84: approaching mastery — base + 1 (same target as 55–69)
      //
      // v11.5: Kept at +1 rather than +2 because tiers alternate
      // base/plus (B6, B6+, M6, M6+...), so +2 would skip to the
      // next school period — too aggressive for "approaching mastery."
      // The ±1 tier window already exposes these students to occasional
      // +2 content. Differentiation from 55–69 comes from the priority
      // system (70–84 families are lower priority, freeing session slots
      // for weaker concepts) and the cleaner band separation from 40–54.
      // Design decision: 70-84 mastery → advance one tier (frees session slots for weaker concepts)
      targetTier = baseTier + 1;
    } else {
      // 85+: mastered — base + 2 (next period, challenge tier)
      targetTier = baseTier + 2;
    }
  }

  // Clamp to allowed tiers for the group (NOT global 1-16)
  const allowedArray = Array.from(allowedTiers).sort((a, b) => a - b);
  if (allowedArray.length === 0) return baseTier;
  const minAllowed = allowedArray[0];
  const maxAllowed = allowedArray[allowedArray.length - 1];
  const clamped = Math.max(minAllowed, Math.min(maxAllowed, targetTier));
  if (clamped !== targetTier) {
    debugLog(`🔒 v11.2: Clamped targetTier ${targetTier} → ${clamped} (allowed: ${minAllowed}-${maxAllowed}) for family ${family.familyId}`);
  }
  return clamped;
}